// Controls
import { BaseComponent } from "@ms/uno-controls/lib/local/controls/BaseComponent";
import { getRTL, Icon } from "@fluentui/react";
import { UnoLeftNavStaticSection } from "./UnoLeftNavStaticSection";
import { UnoLeftNavNewPlanSection } from "./UnoLeftNavNewPlanSection";
import { UnoLeftNavPinnedSection } from "./UnoLeftNavPinnedSection";
import { UnoSwitchViewDropdown } from "@ms/uno-controls/lib/local/controls/presentational/unoSwitchViewDropdown/UnoSwitchViewDropdown";
// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { ComponentNames } from "../../constants/ComponentNames";
import { LeftNavCollapseThreshold } from "@ms/uno-constants/lib/local/UIConstants";
// Logos
import plannerSvg from "@ms/uno-resources/lib/local/images/logos/planner2.svg";
// Styles
import { getUnoLeftNavClassNames, getUnoLeftNavStyles } from "./UnoLeftNav.styles";
import { panelLeftContractProps, panelRightContractProps } from "@ms/uno-controls/lib/local/icons/icons.styles";
import { withTheme } from "@ms/uno-controls/lib/local/utilities/withTheme";
// Utilities
import * as React from "react";
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "../../utilities/CultureUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
/**
 * UnoLeftNav is the root component of this package
 */ class UnoLeftNavInternal extends BaseComponent {
    /**
     * Name of this component. Used for usage logging.
     */ name() {
        return ComponentNames.UnoLeftNav;
    }
    componentDidMount() {
        window.addEventListener("resize", this.handleResize);
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }
    /**
     * Render the Uno leftNav with static section and quick access section
     */ renderComponent() {
        const unoLeftNavClassNames = this.getUnoLeftNavClassNames();
        const className = css(unoLeftNavClassNames.root, this.props.className);
        const appHeader = this.renderAppHeader();
        const staticSection = this.renderLeftNavStaticSection();
        const pinnedSection = this.renderLeftNavPinnedSection();
        const dropdownSection = this.renderDropdownSection();
        const newPlanSection = this.renderNewPlanSection();
        return /*#__PURE__*/ React.createElement("div", {
            className: className
        }, appHeader, !this.props.isLeftNavCollapsed && /*#__PURE__*/ React.createElement("div", {
            className: unoLeftNavClassNames.leftNavContent
        }, dropdownSection, staticSection, pinnedSection), !this.props.isLeftNavCollapsed && newPlanSection);
    }
    /**
     * Method responsible for rendering the app header
     */ renderAppHeader() {
        const { isLeftNavCollapsed } = this.props;
        const { showAppInfo } = this.props;
        const classNames = this.getUnoLeftNavClassNames();
        const appHeaderClassName = classNames.appHeader;
        const appHeaderTitleClassName = classNames.appHeaderTitle;
        const toggleIconProps = isLeftNavCollapsed ? panelRightContractProps : panelLeftContractProps;
        return /*#__PURE__*/ React.createElement("div", {
            className: appHeaderClassName
        }, showAppInfo && !isLeftNavCollapsed && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("img", {
            src: plannerSvg,
            alt: Strings.UnoLeftNav_PlannerLogoText
        }), /*#__PURE__*/ React.createElement("h1", {
            className: appHeaderTitleClassName
        }, Strings.UnoLeftNav_PlannerText)), /*#__PURE__*/ React.createElement(Icon, {
            ...toggleIconProps,
            onClick: this.onToggleLeftNav,
            onKeyDown: (event)=>{
                const key = KeyboardUtilities.getKey(event);
                if (key === KeyCode.ENTER) {
                    this.onToggleLeftNav();
                }
            },
            tabIndex: 0,
            "aria-label": isLeftNavCollapsed ? Strings.UnoLeftNav_ExpandButtonAriaLabel : Strings.UnoLeftNav_CollapseButtonAriaLabel,
            role: "button"
        }));
    }
    /**
     *
     * Method responsible for rendering the dropdown section
     */ renderDropdownSection() {
        return /*#__PURE__*/ React.createElement(UnoSwitchViewDropdown, this.props.switchViewDropdownOptions);
    }
    /**
     * Method responsible for rendering the static section
     */ renderLeftNavStaticSection() {
        const { staticOptions } = this.props;
        return /*#__PURE__*/ React.createElement(UnoLeftNavStaticSection, {
            staticOptions: staticOptions,
            isLeftNavCollapsed: this.props.isLeftNavCollapsed,
            keyCodeMapper: this.keyCodeMapper
        });
    }
    /**
     * Method responsible for rendering the pinned section
     */ renderLeftNavPinnedSection() {
        const { pinnedOptions, shouldRenderPinnedError, onRetryButtonClicked } = this.props;
        return /*#__PURE__*/ React.createElement(UnoLeftNavPinnedSection, {
            pinnedOptions: pinnedOptions,
            isLeftNavCollapsed: this.props.isLeftNavCollapsed,
            keyCodeMapper: this.keyCodeMapper,
            shouldRenderPinnedError: shouldRenderPinnedError,
            onRetryButtonClicked: onRetryButtonClicked
        });
    }
    /**
     * Method responsible for rendering the sticky new plan button
     */ renderNewPlanSection() {
        const { newPlanInfo, calloutDetails } = this.props;
        return /*#__PURE__*/ React.createElement(UnoLeftNavNewPlanSection, {
            newPlanInfo: newPlanInfo,
            isLeftNavCollapsed: this.props.isLeftNavCollapsed,
            keyCodeMapper: this.keyCodeMapper,
            calloutDetails: calloutDetails
        });
    }
    /**
     * Get the classnames associated with the computed styles for the Uno leftNav
     */ getUnoLeftNavClassNames() {
        return getUnoLeftNavClassNames(getUnoLeftNavStyles(this.props.isLeftNavCollapsed, this.props.theme, getRTL(), this.props.showAppInfo));
    }
    /**
     * Key codes used in this control
     */ getKeyCodes() {
        return this.props.customKeyCodes ?? this.defaultKeyCodes;
    }
    constructor(props, context){
        super(props, context), /* Collapse left nav on resize with window inner width of 1024 */ this.handleResize = ()=>{
            // Check if the left nav is already collapsed
            const isLeftNavExplicitlyCollapsed = this.props.isLeftNavCollapsed;
            const newState = isLeftNavExplicitlyCollapsed || window.innerWidth <= LeftNavCollapseThreshold;
            if (this.props.isLeftNavCollapsed !== newState) {
                this.props.setIsLeftNavCollapsed(newState);
            }
        }, /**
     * Toggle the left nav expanded/collapsed state
     */ this.onToggleLeftNav = ()=>{
            this.props.setIsLeftNavCollapsed(!this.props.isLeftNavCollapsed);
        }, /**
     * Keycode mapper for child components
     * @param keyCode Keycode that needs to be mapped to leftNav's keyCodes
     */ this.keyCodeMapper = (keyCode)=>{
            switch(keyCode){
                case KeyCode.ENTER:
                    return this.getKeyCodes().executeActionKeyCode;
                default:
                    return keyCode;
            }
        }, /**
     * Default Key codes used in this control
     */ this.defaultKeyCodes = {
            executeActionKeyCode: KeyCode.ENTER
        };
    }
}
export const UnoLeftNav = withTheme(UnoLeftNavInternal);
